import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AnnouncementForm from '../../components/announcements/AnnouncementForm';
import Markdown from '../../components/recording/Markdown';
import { useConfirm } from '../../components/ConfirmDialog';
import { downloadCsv, downloadXlsx } from '../../lib/exportTable';
import {
  announcementsApi, errorOf, announceChanged, IMPORTANCE_LABEL, STATE_LABEL, stateTone, when
} from '../../lib/announcements';
import '../../components/announcements/Announcements.css';

/**
 * ONE ANNOUNCEMENT (125), for an administrator: what it says, who it is
 * for, when it shows; how many have read it - and, Important, acknowledged
 * it - with the list of who has and who has not (filtered, exported as CSV
 * or Excel); a reminder to those who have not (bell, and email to those who
 * want it, when this server can send mail); change it (a new version once
 * it is showing), end it, or call it off before it starts; its versions and
 * its record.
 *
 * The staff notice's announcement is changed and republished from Settings
 * -> Firm & system -> Retention, so it has no Change button here.
 */
const KIND = { staff: 'Staff', contractor: 'Contractor' };

export const PEOPLE_COLUMNS = [
  { key: 'name', label: 'Name', width: 26 },
  { key: 'kind', label: 'Staff or contractor', width: 16 },
  { key: 'email', label: 'Email', width: 30 },
  { key: 'department', label: 'Department', width: 16 },
  { key: 'read', label: 'Read', width: 20 },
  { key: 'acknowledged', label: 'Acknowledged', width: 20 }
];

export default function AnnouncementDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [people, setPeople] = useState(null);
  const [error, setError] = useState('');
  const [said, setSaid] = useState('');
  const [editing, setEditing] = useState(false);
  const [show, setShow] = useState('all');
  const [busy, setBusy] = useState('');
  const [confirm, confirmElement] = useConfirm();

  const load = useCallback(() => Promise.all([announcementsApi.get(id), announcementsApi.people(id)])
    .then(([d, p]) => { setData(d); setPeople(p.people || []); setError(''); })
    .catch((e) => setError(errorOf(e, 'Could not load the announcement.'))), [id]);
  useEffect(() => { load(); }, [load]);

  const a = data?.announcement;
  const important = a?.importance === 'important';
  const done = (p) => (important ? Boolean(p.acknowledged_at) : Boolean(p.read_at));
  const waiting = (people || []).filter((p) => !done(p));
  const shown = useMemo(() => (people || []).filter((p) => (show === 'done' ? done(p) : show === 'waiting' ? !done(p) : true)),
    [people, show]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error && !data) return <div className="container ann-page"><p className="ann-problem" role="alert">{error}</p></div>;
  if (!a) return <div className="container ann-page"><p className="ann-quiet" aria-busy="true">Loading…</p></div>;

  const exportAs = (kind) => {
    const rows = shown.map((p) => ({
      name: p.name, kind: KIND[p.kind], email: p.email || '', department: p.department || '',
      read: p.read_at ? when(p.read_at) : '', acknowledged: p.acknowledged_at ? when(p.acknowledged_at) : ''
    }));
    const base = `announcement-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'people'}`;
    if (kind === 'csv') downloadCsv(PEOPLE_COLUMNS, rows, base); else downloadXlsx(PEOPLE_COLUMNS, rows, base, 'People');
  };

  const remind = async () => {
    const ok = await confirm({
      title: `Remind ${waiting.length} ${waiting.length === 1 ? 'person' : 'people'}?`,
      message: `Everybody who has not ${important ? 'acknowledged' : 'read'} it gets it in their bell again, and by email if they want announcement emails and this server can send mail.`,
      confirmLabel: 'Send the reminder'
    });
    if (!ok) return;
    setBusy('remind'); setSaid('');
    try {
      const r = await announcementsApi.remind(a.id);
      const e = r.emailed || {};
      setSaid(`Reminded ${r.reminded} ${r.reminded === 1 ? 'person' : 'people'}. ${e.configured === false
        ? 'Nobody was emailed: email is not set up on this server.'
        : `Emailed ${e.sent || 0}${e.off ? `; ${e.off} have announcement emails off` : ''}.`}`);
      await load();
    } catch (err) { setError(errorOf(err, 'The reminder did not go.')); }
    setBusy('');
  };

  const end = async () => {
    const before = a.state === 'scheduled';
    const ok = await confirm({
      title: before ? 'Call it off?' : 'End it now?',
      message: before ? 'It will never show. This is recorded.' : 'Nobody will see it any more - banners and the Announcements tab alike. This is recorded.',
      confirmLabel: before ? 'Call it off' : 'End it', danger: true
    });
    if (!ok) return;
    setBusy('end');
    try { await announcementsApi.end(a.id); announceChanged(); await load(); } catch (err) { setError(errorOf(err, 'Could not end it.')); }
    setBusy('');
  };

  const isNotice = a.source === 'gdpr_notice';
  return (
    <div className="container ann-page" data-testid="announcement-detail">
      <p className="ann-quiet"><Link to="/announcements">← Announcements</Link></p>
      <div className="page-header">
        <div>
          <h1 className="page-title">{a.title}</h1>
          <p className="ann-row-title">
            <span className="ann-chip" data-tone={stateTone(a.state)}>{STATE_LABEL[a.state]}</span>
            <span className={`ann-chip${important ? ' is-important' : ''}`}>{IMPORTANCE_LABEL[a.importance]}</span>
            {isNotice && <span className="ann-chip">Staff privacy notice</span>}
          </p>
          <p className="page-description">
            {`For ${a.audience_words}. ${a.state === 'scheduled' ? 'Starts' : 'Started'} ${when(a.starts_at)}`}
            {a.ends_at ? `, ends ${when(a.ends_at)}` : ', no end'}
            {`. Version ${a.version}.`}
            {a.email ? (a.emailed_at ? ` Emailed ${when(a.emailed_at)}.` : ' To be emailed when it starts.') : ''}
          </p>
        </div>
        <div className="ann-actions">
          {a.state !== 'ended' && !isNotice && <button type="button" className="btn btn-plain" onClick={() => setEditing(true)}>Change</button>}
          {isNotice && <Link className="btn btn-plain" to="/settings/system/retention#notice">Change the notice</Link>}
          {a.state === 'live' && waiting.length > 0 && (
            <button type="button" className="btn btn-primary" disabled={busy === 'remind'} onClick={remind}>Send a reminder</button>
          )}
          {a.state !== 'ended' && (
            <button type="button" className="btn btn-plain" disabled={busy === 'end'} onClick={end}>{a.state === 'scheduled' ? 'Call it off' : 'End it now'}</button>
          )}
        </div>
      </div>
      {error && <p className="ann-problem" role="alert">{error}</p>}
      <p className="ann-flash" role="status" aria-live="polite">{said}</p>

      <div className="ann-stats" data-testid="announcement-stats">
        <div className="ann-stat"><span className="ann-stat-label">It reaches</span><span className="ann-stat-value">{a.counts.total}</span></div>
        <div className="ann-stat"><span className="ann-stat-label">Read</span><span className="ann-stat-value">{a.counts.read}</span></div>
        {important && <div className="ann-stat" data-stat="acknowledged"><span className="ann-stat-label">Acknowledged</span><span className="ann-stat-value">{a.counts.acknowledged}</span></div>}
        <div className="ann-stat" data-stat="waiting"><span className="ann-stat-label">{important ? 'Not acknowledged' : 'Not read'}</span><span className="ann-stat-value">{waiting.length}</span></div>
      </div>

      <section className="ann-card" aria-labelledby="ann-text-t">
        <h2 className="ann-card-title" id="ann-text-t">What it says</h2>
        {String(a.body || '').trim() ? <Markdown text={a.body} className="ann-md" links /> : <p className="ann-quiet">No more to it than the title.</p>}
      </section>

      <section className="ann-card" aria-labelledby="ann-people-t">
        <h2 className="ann-card-title" id="ann-people-t">Who has {important ? 'acknowledged' : 'read'} it</h2>
        <div className="ann-actions">
          <div className="ann-seg" role="group" aria-label="Show">
            {[['all', `Everybody (${people.length})`], ['done', `${important ? 'Acknowledged' : 'Read'} (${people.length - waiting.length})`], ['waiting', `Not yet (${waiting.length})`]].map(([v, l]) => (
              <button key={v} type="button" aria-pressed={show === v} onClick={() => setShow(v)}>{l}</button>
            ))}
          </div>
          <span className="ann-actions" style={{ marginLeft: 'auto', marginBottom: '0.8rem' }}>
            <button type="button" className="btn btn-plain btn-small" onClick={() => exportAs('csv')}>Export CSV</button>
            <button type="button" className="btn btn-plain btn-small" onClick={() => exportAs('xlsx')}>Export Excel</button>
          </span>
        </div>
        {shown.length === 0 ? <p className="ann-quiet">Nobody here.</p> : (
          <div className="ann-table-wrap">
            <table className="ann-table" data-testid="announcement-people">
              <thead><tr><th>Name</th><th>Who</th><th>Read</th>{important && <th>Acknowledged</th>}</tr></thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={`${p.kind}-${p.id}`} data-person={p.id} data-done={done(p) ? 'yes' : 'no'}>
                    <td data-label="Name">{p.name}</td>
                    <td data-label="Who">{KIND[p.kind]}{p.department ? ` · ${p.department}` : ''}</td>
                    <td data-label="Read">{p.read_at ? when(p.read_at) : <span className="ann-quiet">Not yet</span>}</td>
                    {important && <td data-label="Acknowledged">{p.acknowledged_at ? when(p.acknowledged_at) : <span className="ann-quiet">Not yet</span>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {data.versions.length > 1 && (
        <section className="ann-card" aria-labelledby="ann-versions-t">
          <h2 className="ann-card-title" id="ann-versions-t">Versions</h2>
          <ul className="ann-history">
            {data.versions.map((v) => (
              <li key={v.version}>
                <time dateTime={v.created_at}>{when(v.created_at)}</time>
                {`Version ${v.version}: “${v.title}”`}{v.created_by ? ` by ${v.created_by}` : ''}
                {v.requires_ack && important ? ' - everybody asked to acknowledge it' : ''}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="ann-card" aria-labelledby="ann-record-t">
        <h2 className="ann-card-title" id="ann-record-t">Record</h2>
        <ul className="ann-history">
          {data.history.map((h) => (
            <li key={h.id}><time dateTime={h.at}>{when(h.at)}</time>{h.said}<span className="ann-quiet">{` - ${h.actor_name}${h.on_behalf_by ? ` (for ${h.on_behalf_by})` : ''}`}</span></li>
          ))}
        </ul>
      </section>

      {editing && (
        <AnnouncementForm existing={a} onClose={() => setEditing(false)} onSaved={() => {
          setEditing(false); setSaid('Saved.'); announceChanged(); load();
        }} />
      )}
      {confirmElement}
    </div>
  );
}
